import axios from 'axios';
import { getCsrfToken } from './csrf';
import { logger } from './telemetry';

declare module 'axios' {
    interface AxiosRequestConfig {
        _skipAuthRedirect?: boolean;
    }
}

export const api = axios.create({
    baseURL: '/api',
    headers: { 'Content-Type': 'application/json' },
    withCredentials: true,
});

const CSRF_SAFE_METHODS = new Set(['get', 'head', 'options']);

api.interceptors.request.use((config) => {
    const method = (config.method ?? 'get').toLowerCase();
    if (CSRF_SAFE_METHODS.has(method)) {
        return config;
    }
    const token = getCsrfToken();
    if (!token) {
        logger.warn(
            'admin-ui:api-client:requestInterceptor - Missing csrf_token cookie for mutating request',
            { requestUrl: config.url, method },
        );
        return config;
    }
    config.headers = config.headers ?? {};
    if (!config.headers['X-CSRF-Token']) {
        config.headers['X-CSRF-Token'] = token;
    }
    return config;
});

api.interceptors.response.use(
    (response) => response,
    (error) => {
        const status = error?.response?.status;
        const requestUrl = error?.config?.url;
        const skipRedirect = error?.config?._skipAuthRedirect === true;

        if (status === 401) {
            if (skipRedirect) {
                logger.debug('admin-ui:api-client:responseError - 401 received, redirect skipped by caller', {
                    requestUrl,
                });
            } else {
                logger.warn('admin-ui:api-client:responseError - Received 401 response, redirecting to login', {
                    requestUrl,
                    status,
                });
                redirectToLogin();
            }
        } else {
            logger.error('admin-ui:api-client:responseError - Request failed', {
                requestUrl,
                status,
                message: error?.message,
            });
        }
        return Promise.reject(error);
    },
);

const REDIRECT_COOLDOWN_MS = 5000;
const REDIRECT_TS_KEY = 'auth_redirect_ts';

export function redirectToLogin() {
    const lastRedirect = Number(sessionStorage.getItem(REDIRECT_TS_KEY) || '0');
    if (Date.now() - lastRedirect < REDIRECT_COOLDOWN_MS) {
        logger.warn('admin-ui:api-client:redirectToLogin - Redirect suppressed (loop protection)');
        return;
    }
    sessionStorage.setItem(REDIRECT_TS_KEY, String(Date.now()));
    logger.info('admin-ui:api-client:redirectToLogin - Redirecting browser to login endpoint');
    window.location.href = '/api/auth/login';
}
